import { useMemo } from "react";
import { Download, Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { FiltersCard } from "./filters-card";
import { useDashboardUrlState } from "./useDashboardUrlState";

const DASHBOARD_ID = "customer-attribution";
const CSV_DELIMITER = ";";
const TOTAL_COLOR = "var(--color-admin-text-subtle)";
const ACTIVE_COLOR = "var(--color-admin-primary)";
const axisStyle = { fontSize: 11, fill: "var(--color-admin-text-subtle)" };

const GENDERS = ["", "female", "male"] as const;
const GENDER_LABELS: Record<string, string> = {
  "": "Alle kjønn",
  female: "Kvinner",
  male: "Menn",
};

const SOURCE_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  google: "Google",
  friends: "Venner",
  linkedin: "LinkedIn",
  sats: "SATS",
  employeePerk: "Ansattfordel",
  podcast: "Podcast",
  othervdos: "Annet",
  dr_dropin: "Dr Dropin",
};

type AttributionRow = {
  name: string;
  extra_info: string;
  customers: number | string;
  active_members: number | string;
  active_pct: string;
  total_customers: number | string;
  total_active_members: number | string;
};

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().split("T")[0]!;
}

function sourceName(row: Pick<AttributionRow, "name" | "extra_info">): string {
  const base = SOURCE_LABELS[row.name] ??
    row.name.charAt(0).toUpperCase() + row.name.slice(1);
  return row.extra_info ? `${base}: ${row.extra_info}` : base;
}

const SOURCES_COLUMNS: DashboardTableColumn<AttributionRow>[] = [
  {
    id: "source",
    label: "Kundekilde",
    value: (r) => sourceName(r),
    filterable: true,
  },
  {
    id: "customers",
    label: "Kunder",
    value: (r) => r.customers,
    numeric: true,
    align: "right",
  },
  {
    id: "active_members",
    label: "Aktive medlemmer",
    value: (r) => r.active_members,
    numeric: true,
    align: "right",
  },
  {
    id: "active_pct",
    label: "Aktive %",
    value: (r) => Number(r.active_pct),
    numeric: true,
    align: "right",
  },
];

export function CustomerAttributionDashboard() {
  const [{ from, to, ageFrom, ageTo, gender }, update] = useDashboardUrlState({
    from: isoDaysAgo(365),
    to: isoDaysAgo(0),
    ageFrom: "",
    ageTo: "",
    gender: "",
  });

  const params = useMemo(
    () => ({
      from,
      to,
      ageFrom: ageFrom === "" ? undefined : Number(ageFrom),
      ageTo: ageTo === "" ? undefined : Number(ageTo),
      gender: gender === "" ? undefined : gender,
    }),
    [from, to, ageFrom, ageTo, gender],
  );
  const sources = useDashboardChart<typeof params, AttributionRow>(
    DASHBOARD_ID,
    "sources",
    params,
  );
  const rows = sources.data ?? [];
  const sourcesTable = useDashboardTable(
    "sources",
    SOURCES_COLUMNS,
    rows,
  );
  const totals = useMemo(
    () => ({
      customers: Number(rows[0]?.total_customers ?? 0),
      active: Number(rows[0]?.total_active_members ?? 0),
    }),
    [rows],
  );
  const chartRows = rows.slice(0, 12).map((row) => ({
    source: sourceName(row),
    customers: Number(row.customers),
    active: Number(row.active_members),
  }));
  const customersKpi = sources.isLoading
    ? "…"
    : sources.isError
      ? "Feil"
      : totals.customers;
  const activeKpi = sources.isLoading
    ? "…"
    : sources.isError
      ? "Feil"
      : totals.active;
  const activeRateKpi = sources.isLoading
    ? "…"
    : sources.isError
      ? "Feil"
      : totals.customers > 0
        ? `${((totals.active / totals.customers) * 100).toLocaleString("no-NO", {
            maximumFractionDigits: 1,
          })}%`
        : "—";

  const exportCsv = () => {
    // The export follows the table's view — same column filter and sort.
    const csv = toCsv(
      ["Kundekilde", "Kunder", "Aktive medlemmer", "Aktive %"],
      sourcesTable.viewRows.map((row) => [
        sourceName(row),
        row.customers,
        row.active_members,
        row.active_pct.replace(".", ","),
      ]),
      CSV_DELIMITER,
      { neutralizeFormulas: true },
    );
    downloadCsv(csv, `kundekilder-${from}-${to}.csv`, { bom: true });
  };

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(event) => update({ from: event.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(event) => update({ to: event.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Alder fra</span>
            <Input
              type="number"
              min="0"
              max="150"
              value={ageFrom}
              onChange={(event) => update({ ageFrom: event.target.value })}
              placeholder="Alle"
              className="w-28"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Alder til</span>
            <Input
              type="number"
              min="0"
              max="150"
              value={ageTo}
              onChange={(event) => update({ ageTo: event.target.value })}
              placeholder="Alle"
              className="w-28"
            />
          </label>
        </div>
        <div>
          <p className="mb-2 text-xs text-admin-text-subtle">Kjønn</p>
          <div className="flex flex-wrap gap-2">
            {GENDERS.map((value) => (
              <Button
                key={value || "all"}
                variant={gender === value ? "default" : "outline"}
                size="sm"
                aria-pressed={gender === value}
                onClick={() => update({ gender: value })}
              >
                {GENDER_LABELS[value]}
              </Button>
            ))}
          </div>
        </div>
        <p className="text-xs text-admin-text-subtle">
          Perioden gjelder når kundekilden ble registrert. Aldersfilteret bruker
          kundens alder i dag.
        </p>
      </FiltersCard>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiTile
          label="Kunder med registrert kilde"
          value={customersKpi}
        />
        <KpiTile
          label="Aktive medlemmer i dag"
          value={activeKpi}
        />
        <KpiTile label="Andel aktive" value={activeRateKpi} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Kundekilder</CardTitle>
          <p className="mt-1 text-xs text-admin-text-subtle">
            De 12 største kildene i valgt kohort. Tabellen under viser alle.
          </p>
        </CardHeader>
        <CardContent>
          {sources.isLoading ? (
            <LoadingBlock />
          ) : sources.isError ? (
            <ErrorBlock message={sources.error?.message} />
          ) : chartRows.length === 0 ? (
            <EmptyBlock />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(280, chartRows.length * 42)}>
              <BarChart
                data={chartRows}
                layout="vertical"
                margin={{ top: 8, right: 20, left: 24, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-admin-card-border)" />
                <XAxis type="number" allowDecimals={false} tick={axisStyle} />
                <YAxis
                  type="category"
                  dataKey="source"
                  width={150}
                  tick={axisStyle}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-admin-card)",
                    borderColor: "var(--color-admin-card-border)",
                    color: "var(--color-admin-text-primary)",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="customers" name="Kunder" fill={TOTAL_COLOR} radius={[0, 3, 3, 0]} />
                <Bar dataKey="active" name="Aktive i dag" fill={ACTIVE_COLOR} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Alle kundekilder</CardTitle>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </CardHeader>
        <CardContent>
          {sources.isLoading ? (
            <LoadingBlock />
          ) : sources.isError ? (
            <ErrorBlock message={sources.error?.message} />
          ) : rows.length === 0 ? (
            <EmptyBlock />
          ) : (
            <DashboardTable
              table={sourcesTable}
              columns={SOURCES_COLUMNS}
              renderRow={(row) => (
                <TableRow key={`${row.name}:${row.extra_info}`}>
                  <TableCell className="font-medium text-admin-text-primary">
                    {sourceName(row)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Number(row.customers).toLocaleString("no-NO")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Number(row.active_members).toLocaleString("no-NO")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {Number(row.active_pct).toLocaleString("no-NO", {
                      maximumFractionDigits: 1,
                    })}%
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
      <p className="text-xs text-admin-text-subtle">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-admin-text-primary">
        {typeof value === "number" ? value.toLocaleString("no-NO") : value}
      </p>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-admin-text-subtle">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock() {
  return <p className="py-6 text-center text-sm text-admin-text-subtle">Ingen data i valgt kohort.</p>;
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
